import React, { useState, useEffect } from 'react';
import { CircularProgress, Button, Box, Typography, TextField, MenuItem, Grid, FormControl, InputLabel, Select, Switch, Chip, Dialog, DialogActions, DialogTitle, DialogContent, useMediaQuery, useTheme, Divider } from '@mui/material';
import Paper from '@mui/material/Paper';
import {
    useNotify,
    useTranslate,
} from 'react-admin'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import FilterListIcon from '@mui/icons-material/FilterList';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Checkbox from '@mui/material/Checkbox';
import 'dayjs/locale/es'
import { businessUnitServices } from 'src/services/reservation/businessUnit/businessUnit.service'
import dayjs from 'dayjs';
import Tooltip from '@mui/material/Tooltip';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { isGecko } from '@fingerprintjs/fingerprintjs';

function CustomDivider(content) {
    return (
        <Box display="flex" alignItems="center" sx={{ width: '98%', margin: '15px 0 auto 15px' }}>
            <Typography variant="body1" sx={{ mr: 2, color: '#a9afc8', fontWeight: 'bold' }}>
                {content}
            </Typography>
            <Divider sx={{ flexGrow: 1, borderBottomWidth: 2, borderColor: 'rgba(222,222,222,255)' }} />
        </Box>
    );
}

function ReservationModal({
    open,
    handleClose,
    handleSaveReservations,
    reservationsList,
    business_unit_id,
    setReservationsList,
    previousReservations,
    paymentDateSelected,
    selectedPaymentType,
    currencySelected
}) {
    const theme = useTheme(); // Obtener el tema actual
    const notify = useNotify()
    const translate = useTranslate()
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm')); // Determinar si es pantalla pequeña
    const [reservationsSelected, setReservationsSelected] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [errors, setErrors] = useState({});
    const [filters, setFilters] = useState({
        reservationNumber: '',
        name: '',
        providerCode: '',
        month: ''
    });
    const [isLoadingFilter, setIsLoadingFilter] = useState(false)
    const [showMessageReservationsResult, setShowMessageReservationsResult] = useState(false)
    const [urlPrevious, setUrlPrevious] = useState(null);
    const [urlNext, setUrlNext] = useState(null);

    const handleSelectAll = (e) => {
        const isChecked = e.target.checked;
        setSelectAll(isChecked);

        const selectableReservations = reservationsList.filter(reservation => !reservation.disabled_data);

        if (isChecked) {
            // Agregar reservas visibles que no están seleccionadas actualmente
            setReservationsSelected((prevSelected) => {
                const newSelections = selectableReservations.filter(
                    reservation => !prevSelected.some(item => item.id === reservation.id)
                );
                return [...prevSelected, ...newSelections];
            });
        } else {
            // Remover solo las reservas visibles
            setReservationsSelected((prevSelected) =>
                prevSelected.filter(item => !selectableReservations.some(res => res.id === item.id))
            );
        }
    };

    const handleSelectReservation = (reservation) => {
        const isSelected = reservationsSelected.some(item => item.id === reservation.id);

        if (isSelected) {
            setReservationsSelected(prevSelected => prevSelected.filter(item => item.id !== reservation.id));
        } else {
            setReservationsSelected(prevSelected => [...prevSelected, reservation]);
        }
    };

    const handleDeleteChip = (reservationId) => {
        setReservationsSelected(prevSelected => prevSelected.filter(item => item.id !== reservationId));
    };

    const resetForm = () => {
        setReservationsList([])
        setReservationsSelected([])
        setShowMessageReservationsResult(false)
        setFilters({  // Limpiar los filtros
            reservationNumber: '',
            name: '',
            providerCode: '',
            month: ''
        });
    };

    const handleModalClose = () => {
        resetForm();
        handleClose();
    };

    const onSave = () => {
        const errorsAux = {};
        setErrors(errorsAux)

        const pivotBase = reservationsSelected[0].base
        for(let reservation of reservationsSelected){
            if(reservation.base !== pivotBase){
                notify('ra.notification.different_bases_error', {type:'error'})
                return
            }
        }
        if (Object.keys(errorsAux).length === 0) {
            handleSaveReservations(reservationsSelected);
            handleModalClose();
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFilter = async () => {
        setUrlPrevious(null)
        setUrlNext(null)
        try {
            setIsLoadingFilter(true)
            const formattedMonth = filters.month ? dayjs(filters.month, 'MM/YYYY').format('MM') : null;
            const reservationNumbers = filters.reservationNumber?.replace(/\s+/g, '') || null;
            const formattedDate = new Date(paymentDateSelected).toLocaleDateString('en-CA') || null;

            const reservationsFilter = await businessUnitServices.getReservationByBusinessUnitPaymentCompress({
                business_unit_id: business_unit_id,
                optional_get_user: false,
                offset: 0,
                limit: 50,
                status: 'OPEN',
                reservation_number_multiple: reservationNumbers,
                month: formattedMonth || null,
                pax_name: filters.name || null,
                provider_code: filters.providerCode || null,
                destination_currency: currencySelected || null,
                payment_date: formattedDate,
                payment_type: selectedPaymentType
            })

            if (reservationsFilter.prev_url) {
                setUrlPrevious(reservationsFilter.prev_url)
            }
            if (reservationsFilter.next_url) {
                setUrlNext(reservationsFilter.next_url)
            }

            const previousReservationIds = new Set(previousReservations.map(res => res.id));
            const uniqueReservations = reservationsFilter.data?.filter(res =>
                !previousReservationIds.has(res.id)
            );

            setReservationsList(uniqueReservations);
            setShowMessageReservationsResult(true)
        } catch (error) {
            setReservationsList([])
            setIsLoadingFilter(false);
            setUrlPrevious(null)
            setUrlNext(null)
        } finally {
            setIsLoadingFilter(false); // Siempre lo establece en false cuando la promesa se completa (éxito o error)
        }
    };

    const handlePagination = async (url) => {
        setUrlPrevious(null)
        setUrlNext(null)
        try {
            setIsLoadingFilter(true)
            const reservationsFilter = await businessUnitServices.getReservationByBusinessUnitPaymentCompressPagination(url)

            if (reservationsFilter.prev_url) {
                setUrlPrevious(reservationsFilter.prev_url)
            }
            if (reservationsFilter.next_url) {
                setUrlNext(reservationsFilter.next_url)
            }

            const previousReservationIds = new Set(previousReservations.map(res => res.id));
            const uniqueReservations = reservationsFilter.data?.filter(res =>
                !previousReservationIds.has(res.id)
            );

            setReservationsList(uniqueReservations);
            setShowMessageReservationsResult(true)

            // Verificar si todas las reservas actuales están seleccionadas
            const selectableReservations = uniqueReservations.filter(reservation => !reservation.disabled_data);
            const allSelected = selectableReservations.every(reservation =>
                reservationsSelected.some(selected => selected.id === reservation.id)
            );
            setSelectAll(allSelected);

        } catch (error) {
            setReservationsList([])
            setIsLoadingFilter(false);
            setUrlPrevious(null)
            setUrlNext(null)
        } finally {
            setIsLoadingFilter(false); // Siempre lo establece en false cuando la promesa se completa (éxito o error)
        }
    };

    const getLastEightMonths = () => {
        const months = [];
        const today = dayjs();

        for (let i = 0; i < 8; i++) {
            const date = today.subtract(i, 'month');
            months.push({
                value: date.format('MM/YYYY'),
                label: date.format('MMMM YYYY') // Ejemplo: "Octubre 2023"
            });
        }

        return months;
    };

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    { handleModalClose() }
                }
            }}
            fullWidth
            maxWidth="lg" // Limita el tamaño máximo en pantallas grandes
            fullScreen={fullScreen} // Pantalla completa en móviles
            disableEscapeKeyDown
        >
            {/* HEADER */}
            <DialogTitle id="modal-title" sx={{ textAlign: 'center' }}>
                {translate('ra.title.reservationSelection')}
            </DialogTitle>

            <DialogContent >
                <Grid container spacing={2} style={{ height: '100%' }}>
                    {CustomDivider(translate('ra.action.searchReservations'))}
                    <Grid container item md={12} lg={12} xs={12} alignItems="center" className='payment-register'>
                        <EditInputsWrap label={translate('ra.field.reservationNumber')} md={3} lg={3} xs={6}>
                            <Tooltip arrow title={translate('ra.tooltip.separateReservationsForMultiSearch')} >
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    name="reservationNumber"
                                    value={filters.reservationNumber}
                                    onChange={handleInputChange}
                                    InputProps={{
                                        sx: { height: '40px' }
                                    }}
                                />
                            </Tooltip>
                        </EditInputsWrap>
                        <EditInputsWrap label={translate('ra.field.name')} md={3} lg={3} xs={6}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                type="text"
                                name="name"
                                value={filters.name}
                                onChange={handleInputChange}
                                InputProps={{
                                    sx: { height: '40px' }
                                }}
                            />
                        </EditInputsWrap>
                        <EditInputsWrap label={translate('ra.field.supplierCode')} md={2} lg={2} xs={6}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                type="text"
                                name="providerCode"
                                value={filters.providerCode}
                                onChange={handleInputChange}
                                InputProps={{
                                    sx: { height: '40px' }
                                }}
                            />
                        </EditInputsWrap>
                        <EditInputsWrap label={translate('ra.field.month')} md={2} lg={2} xs={6}>
                            <FormControl fullWidth variant="outlined" size="small">
                                <Select
                                    name="month"
                                    value={filters.month}
                                    onChange={handleInputChange}
                                    sx={{ height: '40px' }}
                                >
                                    <MenuItem key={'55'} value={null}>
                                        Sin selección
                                    </MenuItem>
                                    {getLastEightMonths().map((month) => (
                                        <MenuItem key={month.value} value={month.value}>
                                            {month.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </EditInputsWrap>
                        <EditInputsWrap md={2} lg={2} xs={6}>
                            <Button
                                style={{ marginBottom: '5px', borderWidth: '2px', fontWeight: 'bold' }}
                                variant="outlined"
                                className="myButton"
                                onClick={handleFilter}  // Agregar la función de filtro
                                disabled={isLoadingFilter}
                            >
                                <FilterListIcon /> {translate('ra.action.search')}
                            </Button>
                        </EditInputsWrap>
                    </Grid>
                </Grid>

                <Grid container spacing={2} style={{ height: '100%' }}>
                    {CustomDivider(translate('ra.title.selectReservations'))}
                    <TableContainer style={{ margin: '1%' }}>
                        <Table sx={{ minWidth: 650, borderCollapse: 'collapse' }} size="small" >
                            <TableHead >  {/* Elimina el borde del encabezado */}
                                <TableRow className='cabecera'>
                                    <TableCell align="center" >
                                        <Checkbox
                                            sx={{
                                                color: '#f53b1b',
                                                '&.Mui-checked': { color: '#f53b1b' },
                                            }}
                                            checked={
                                                reservationsSelected.filter(reservation =>
                                                    reservationsList.some(listItem => listItem.id === reservation.id && !listItem.disabled_data)
                                                ).length === reservationsList.filter(reservation => !reservation.disabled_data).length &&
                                                reservationsList.filter(reservation => !reservation.disabled_data).length > 0
                                            }
                                            indeterminate={
                                                reservationsSelected.filter(reservation =>
                                                    reservationsList.some(listItem => listItem.id === reservation.id && !listItem.disabled_data)
                                                ).length > 0 &&
                                                reservationsSelected.filter(reservation =>
                                                    reservationsList.some(listItem => listItem.id === reservation.id && !listItem.disabled_data)
                                                ).length < reservationsList.filter(reservation => !reservation.disabled_data).length
                                            }
                                            onChange={handleSelectAll}
                                        />
                                    </TableCell>
                                    <TableCell align="center"><b>{translate('ra.field.reservationNumber')} </b></TableCell>
                                    <TableCell align="center"><b> {translate('ra.field.totalAmount')} </b></TableCell>
                                    <TableCell align="center"><b> {translate('ra.field.paidAmount')} </b></TableCell>
                                    <TableCell align="center"><b> {translate('ra.field.pendingAmount')}  </b></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoadingFilter ? ( // Condición para mostrar el loading
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ padding: "20px 0" }}>
                                            <CircularProgress /> {/* Muestra el circular progress */}
                                        </TableCell>
                                    </TableRow>
                                ) : reservationsList.length > 0 ? ( // Si no está cargando, muestra las reservas
                                    reservationsList.map(reservation => (
                                        <TableRow key={`reservation-${reservation.id}`}>
                                            <TableCell align="center">
                                                <Checkbox
                                                    sx={{ color: '#f53b1b', '&.Mui-checked': { color: '#f53b1b' } }}
                                                    checked={reservationsSelected.some(item => item.id === reservation.id)} // Verifica si está seleccionada
                                                    onChange={() => handleSelectReservation(reservation)}
                                                    disabled={reservation.disabled_data}
                                                />
                                            </TableCell>
                                            <TableCell component="th" scope="row" align="center">
                                                {reservation.number}
                                            </TableCell>
                                            <TableCell align="center">
                                                {reservation.disabled_data
                                                    ? translate('ra.message.unavailable')
                                                    : `${reservation.reservation_prices.origin_currency} ${new Intl.NumberFormat('es-AR', {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    }).format(parseFloat(reservation.reservation_prices.total_amount))}`
                                                }
                                            </TableCell>

                                            <TableCell align="center">
                                                {reservation.disabled_data
                                                    ? translate('ra.message.unavailable')
                                                    : `${reservation.reservation_prices.origin_currency} ${new Intl.NumberFormat('es-AR', {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    }).format(parseFloat(reservation.reservation_prices.paid_amount))}`
                                                }
                                            </TableCell>

                                            <TableCell align="center">
                                                {reservation.disabled_data
                                                    ? translate('ra.message.unavailable')
                                                    : `${reservation.reservation_prices.origin_currency} ${new Intl.NumberFormat('es-AR', {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    }).format(parseFloat(reservation.reservation_prices.pending_amount))}`
                                                }
                                            </TableCell>
                                        </TableRow>
                                    )

                                    )
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ lineHeight: "4.43", padding: "20px 0" }}>
                                            {showMessageReservationsResult ?
                                                translate('ra.message.noOpenReservationsFound')
                                                :
                                                translate('ra.message.enableFilter')
                                            }
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>

                {reservationsList.length > 0 &&
                    <Grid
                        container
                        justifyContent="center"
                        alignItems="center"
                        sx={{ marginTop: '20px', gap: '8px' }}
                    >
                        {urlPrevious && (
                            <Button
                                onClick={() => handlePagination(urlPrevious)}
                                variant="outlined"
                                sx={{
                                    color: '#555555',
                                    backgroundColor: '#dbe1f1',  // Color de fondo similar al del hover
                                    borderColor: '#a9afc8',
                                    padding: '6px 16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    borderRadius: '8px',
                                    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        backgroundColor: '#eef2fb',
                                        borderColor: '#8a99d6',
                                        transform: 'scale(1.05)',
                                    },
                                }}
                            >
                                <ArrowBackIcon />
                                {translate('ra.navigation.prev')}
                            </Button>
                        )}

                        {urlNext && (
                            <Button
                                onClick={() => handlePagination(urlNext)}
                                variant="outlined"
                                sx={{
                                    color: '#555555',
                                    backgroundColor: '#dbe1f1',  // Color de fondo similar al del hover
                                    borderColor: '#717fb8',
                                    padding: '6px 16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    borderRadius: '8px',
                                    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        backgroundColor: '#eef2fb',
                                        borderColor: '#5c6fa5',
                                        transform: 'scale(1.05)',
                                    },
                                }}
                            >
                                {translate('ra.navigation.next')}
                                <ArrowForwardIcon />
                            </Button>
                        )}

                    </Grid>
                }

                <Grid container spacing={2} style={{ height: '100%' }}>
                    {CustomDivider(translate('ra.title.selectedReservations'))}
                    <Paper
                        elevation={0}
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap', // Permitir que los chips se distribuyan en varias filas
                            width: '100%',
                            border: '1px solid rgba(0, 0, 0, 0.12)',
                            borderRadius: '8px',
                            padding: '16px',
                            boxSizing: 'border-box',
                            margin: '2%',
                        }}
                    >
                        {reservationsSelected.length > 0 ? (
                            reservationsSelected.map(reservation => (
                                <Chip
                                    key={'chip' + reservation.id}
                                    label={`Reserva ${reservation.number}`}
                                    onDelete={() => handleDeleteChip(reservation.id)}
                                    variant="outlined"
                                    sx={{
                                        backgroundColor: '#f5f5f5',
                                        border: '1px solid #ccc',
                                        padding: '8px',
                                        fontSize: '14px',
                                        '& .MuiChip-deleteIcon': {
                                            color: '#ff6b6b',
                                        },
                                        margin: '4px', // Espaciado entre chips
                                    }}
                                />
                            ))
                        ) : (
                            <Typography variant="body2" color="textSecondary">
                                {translate('ra.message.noReservationsSelected')}
                            </Typography>
                        )}
                    </Paper>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={handleModalClose}
                    sx={{
                        color: '#fff',
                        backgroundColor: '#d9534f', // Un rojo más suave que combine con #a9afc8
                        fontWeight: 'bold',
                        borderRadius: '8px',
                        padding: '8px 24px',
                        '&:hover': {
                            backgroundColor: '#c9302c', // Un poco más oscuro en hover
                        },
                    }}
                >
                    {translate('ra.action.cancel')}
                </Button>

                <Button
                    onClick={onSave}
                    sx={{
                        color: '#fff',
                        backgroundColor: '#8a99d6', // Color principal de la combinación
                        fontWeight: 'bold',
                        borderRadius: '8px',
                        padding: '8px 24px',
                        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)', // Sombra para darle un toque elegante
                        '&:hover': {
                            backgroundColor: '#717fb8', // Un poco más oscuro en hover para resaltar la interacción
                        },
                    }}
                >
                    {translate('ra.action.save')}
                </Button>
            </DialogActions>
        </Dialog>
    )

}

export default ReservationModal;